import type { AutomationTriggerType } from '@adweb/shared';
import clsx from 'clsx';
import { CalendarClock, FileText, Mail, MessageSquare, Webhook } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface Meta {
  label: string;
  icon: LucideIcon;
  cls: string;
}

// Nature d'une automatisation (AutomationTriggerType). Aligné sur l'enum n8n.
const META: Record<AutomationTriggerType, Meta> = {
  FORM: { label: 'Formulaire', icon: FileText, cls: 'bg-brand-50 text-brand' },
  SCHEDULED: { label: 'Planifié', icon: CalendarClock, cls: 'bg-violet-50 text-violet-700' },
  WEBHOOK: { label: 'Webhook', icon: Webhook, cls: 'bg-emerald-50 text-emerald-700' },
  CHAT: { label: 'Chat', icon: MessageSquare, cls: 'bg-accent-50 text-accent' },
  EMAIL: { label: 'E-mail', icon: Mail, cls: 'bg-amber-50 text-amber-700' },
};

const FALLBACK: Meta = { label: 'Autre', icon: FileText, cls: 'bg-slate-100 text-slate-600' };

/** Libellé, icône et couleurs associés à un type d'automatisation (badges, tuiles). */
export function triggerTypeMeta(type: AutomationTriggerType | string): Meta {
  return META[type as AutomationTriggerType] ?? { ...FALLBACK, label: type };
}

export function TriggerTypeBadge({ type }: { type: AutomationTriggerType | string }) {
  const meta = triggerTypeMeta(type);
  const Icon = meta.icon;
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
        meta.cls,
      )}
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      {meta.label}
    </span>
  );
}

// Libellé FR du déclencheur d'une exécution (ExecutionTrigger).
const EXECUTION_TRIGGER_LABELS: Record<string, string> = {
  MANUAL: 'Manuel',
  SCHEDULED: 'Planifié',
  FORM: 'Formulaire',
  WEBHOOK: 'Webhook',
  CHAT: 'Chat',
  EMAIL: 'E-mail',
  EVENT: 'Autre',
};

export function executionTriggerLabel(trigger: string): string {
  return EXECUTION_TRIGGER_LABELS[trigger] ?? trigger;
}
